import type { HelpContent } from './types';

export const projectsHelp: HelpContent = {
  pageTitle: 'Projects',
  description: 'The Projects page lists all projects you have access to and is the main starting point for finding, creating, and managing projects.',

  functionality: {
    overview:
      'This page shows your company\'s projects in either a grid or grouped list view. You can filter, search, and sort projects, then open any project to see its full details. Users with the right permissions can create new projects from here.',
    keyFeatures: [
      {
        label: 'Project list and grid views',
        detail: 'Switch between a card grid and a grouped list view to suit how you prefer to scan your projects.',
      },
      {
        label: 'Search and filter',
        detail: 'Search by project name or client, and apply filters for status, client, date range, and more.',
      },
      {
        label: 'Create project',
        detail: 'Open the new project form to set up a project with its key details, budget, and team.',
        requiredPermission: 'projectActions.create',
      },
      {
        label: 'Project health indicators',
        detail: 'Each project card shows a health indicator (green, amber, red) based on budget consumption against the thresholds configured in Project Settings.',
      },
      {
        label: 'Bulk actions',
        detail: 'Select multiple projects to update their status or assign a key person in a single action.',
        requiredPermission: 'projectActions.changeStatus',
      },
      {
        label: 'Sorting and grouping',
        detail: 'Sort projects by name, start date, end date, or budget, and group them by client, status, or department.',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Search bar',
        description: 'Filters the project list as you type — matches against project name and client name.',
      },
      {
        name: 'Filter button',
        description: 'Opens the filter sheet where you can narrow projects by status, client, date range, project manager, and other criteria.',
      },
      {
        name: 'Sort controls',
        description: 'Changes the order in which projects are displayed.',
      },
      {
        name: 'Group by selector',
        description: 'Groups projects by a selected attribute such as client, status, or department.',
      },
      {
        name: 'Create Project button',
        description: 'Opens the new project creation panel.',
        requiredPermission: 'projectActions.create',
      },

      {
        name: 'Bulk select checkboxes',
        description: 'Appear when you hover over a card — select multiple projects to trigger bulk actions.',
      },
    ],
    useCases: [
      'A project manager opens Projects to find all their active projects and check which ones have a critical budget health status.',
      'An administrator creates a new project for a client engagement that has just been signed off.',
      'A finance lead filters by status "Active" and sorts by end date to identify projects that are finishing soon.',
      'A team lead selects multiple projects to bulk-update their status to "On Hold" during a company-wide pause.',
    ],
  },

  userGuide: {
    gettingStarted:
      'The projects you see depend on your role\'s project visibility setting. Administrators and executives see all projects. Project managers see only projects they manage or are allocated to.',
    steps: [
      {
        title: 'How do I find a specific project?',
        items: [
          {
            text: 'Type the project name or client name into the search bar at the top of the page.',
            tip: 'The list filters in real time as you type — no need to press Enter.',
          },
          {
            text: 'If you cannot find it by name, click Filter and apply relevant criteria such as status or date range.',
            note: 'If a project does not appear in your list, it may be outside your role\'s project visibility scope. Ask an administrator.',
          },
        ],
      },
      {
        title: 'How do I create a new project?',
        requiredPermission: 'projectActions.create',
        items: [
          { text: 'Click Create Project in the top-right area of the page.' },
          { text: 'Fill in the required fields: project name, client, start date, and project manager.' },
          {
            text: 'Complete any additional fields your company has configured as required in Project Settings.',
            note: 'Required fields are marked with a red asterisk. You cannot save the project until they are filled in.',
          },
          { text: 'Click Save to create the project.' },
          {
            text: 'The project opens on its Overview tab immediately after creation.',
            tip: 'You can set the project to Draft status during creation if you are not ready to share it with the team yet.',
          },
        ],
      },
      {
        title: 'How do I filter projects?',
        items: [
          { text: 'Click the Filter button near the search bar.' },
          { text: 'Select values for the filter criteria you want to apply: status, client, project manager, date range, etc.' },
          { text: 'The project list updates immediately as you select filters.' },
          {
            text: 'Click Clear Filters to remove all active filters.',
            tip: 'Applied filters are shown as badges near the search bar so you can see at a glance what is active.',
          },
        ],
      },
    ],
    proTips: [
      'Use the Group By option to organise projects by client — this gives you a quick view of your entire workload per account.',
      'Pin frequently visited projects as favourites (star icon on the card) so they appear at the top of the list.',
      'The health indicator on each card is calculated from the budget thresholds in Project Settings. If the thresholds feel too sensitive, ask an administrator to adjust them.',
    ],
  },

  faq: [
    {
      question: 'Why can I only see some projects and not all of them?',
      answer:
        'Your role\'s project visibility setting controls which projects you can see. "All projects" shows every project. "Managed & Allocated" restricts the view to projects where you are a key person or resource. Ask an administrator to change your role if you need broader access.',
    },
    {
      question: 'How do I archive a project?',
      answer:
        'Open the project and change its status to Archived (or use the status change option in the bulk actions bar). Archived projects are hidden from the default view but can be found using the status filter.',
      requiredPermission: 'projectActions.archive',
    },
    {
      question: 'Can I delete a project?',
      answer:
        'Project deletion is controlled by your role permissions. If you have delete access, a delete option is available in the project actions menu. Deleted projects cannot be recovered.',
      requiredPermission: 'projectActions.delete',
    },
    {
      question: 'What does the health indicator on each project card mean?',
      answer:
        'The health indicator (green, amber, red) is based on how much of the budget has been consumed relative to the thresholds set in Project Settings. Green means healthy, amber means approaching a threshold, and red means the threshold has been exceeded.',
    },
    {
      question: 'How do I see archived projects?',
      answer: 'Click Filter, then set the Status filter to include "Archived". Archived projects are hidden by default to keep the list clean.',
    },
  ],

  relatedPages: [
    { label: 'Portfolio', path: '/portfolio' },
    { label: 'Resource Allocation', path: '/resources' },
    { label: 'Project Transactions', path: '/project-transactions' },
    { label: 'Project Settings', path: '/settings/project' },
  ],
};
